import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Wrench } from "lucide-react";

// 2026-09 R4 no-bypass — the ONE frontend rendering of the backend's
// non-overridable 409 `workflow_setup_required` (process_workflow.
// workflow_setup_block): a Case that is not mapped onto the canonical
// workflow registry can't change stage through ANY path until an
// administrator completes its workflow setup. Deliberately distinct from the
// red "blocked transition" panels (required_payment / required_approval /
// ...): it is not a business gate the employee can satisfy, there is never an
// override control, and only an admin (admin.config) is offered the way
// forward.
export const WORKFLOW_SETUP_BLOCKER = "workflow_setup_required";
// Single place to update when the admin setup surface moves. 2026-09: the
// "Existing Case Workflow Setup" review screen (pages/CaseWorkflowSetupPage),
// reached from the admin-only banner on Process / Cases — no longer a
// permanent Administration tab.
export const WORKFLOW_SETUP_ADMIN_ROUTE = "/process/workflow-setup";
// Deep link that opens the review screen focused on one Case.
export const workflowSetupRouteFor = (caseId) =>
  (caseId ? `${WORKFLOW_SETUP_ADMIN_ROUTE}?case=${encodeURIComponent(caseId)}` : WORKFLOW_SETUP_ADMIN_ROUTE);

export const isWorkflowSetupBlock = (detail) =>
  !!detail && typeof detail === "object" && detail.blocker_type === WORKFLOW_SETUP_BLOCKER;

export function WorkflowSetupRequiredNotice({ detail, attempted = false, testId }) {
  const { hasCap } = useAuth();
  if (!detail) return null;
  const isAdmin = hasCap("admin.config");
  const id = testId || "workflow-setup-required";
  // Only a Case the setup screen can actually resolve ("not_mapped") gets
  // the link; a stale workflow version / a product with no workflow yet
  // needs a workflow update, not setup.
  const resolvable = !detail.reason_code || detail.reason_code === "not_mapped";
  return (
    <Card role="alert" data-testid={id} data-attempted={attempted ? "true" : "false"}
      className="p-3 border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/10">
      <div className="flex items-start gap-2">
        <Wrench className="h-4 w-4 mt-0.5 text-[hsl(var(--warning))] shrink-0" />
        <div className="text-xs space-y-1 min-w-0">
          <div className="font-semibold text-sm" data-testid={`${id}-title`}>
            Workflow setup required{attempted ? " — stage change blocked" : ""}
          </div>
          <p className="text-muted-foreground">
            This Case was created before the new workflow and hasn&apos;t been set up on it
            yet{detail.current_stage ? <> (recorded stage: <b>{detail.current_stage}</b>)</> : null}, so its
            stage can&apos;t be changed from here — by anyone. There is no override.
          </p>
          {isAdmin && resolvable ? (
            <p>
              <Link to={workflowSetupRouteFor(detail.case_id)} className="underline decoration-dotted text-primary"
                data-testid={`${id}-admin-link`}>
                Confirm this Case&apos;s stage in Existing Case Workflow Setup →
              </Link>
            </p>
          ) : isAdmin ? (
            <p className="text-muted-foreground" data-testid={`${id}-needs-update`}>
              This product&apos;s workflow must be updated before this Case can be set up.
            </p>
          ) : (
            <p className="text-muted-foreground" data-testid={`${id}-ask-admin`}>
              Ask an administrator to complete workflow setup for this Case.
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
